import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IDBProduct } from '../../interfaces/product';
import { AnalyticsService } from '../../services/analytics.service';
import { StockAlertAnswer, StockAlertService, StockAlertsMine } from '../../services/stock-alert.service';
import { StorageService } from '../../services/storage.service';
import { UserService } from '../../services/user.service';
import { CultureService } from '../../culture/culture.service';

/// What this browser remembers of the alerts it made - a convenience, never the record: the server has them.
interface KeptAlert { t: string; e: string; at: number; }

/// "Email me when it's back" for the variant on screen of a sold-out product (StockAlertService). A signed-in shopper
/// asks with one press and the account's address; a guest types one. Once set it says where the email will go, with a
/// way to cancel. Drawn in the browser only: the server render does not know who is looking, and a form that changed
/// shape when the page woke up would be one more thing jumping on screen - it keeps the form's height instead.
@Component({
    selector: 'app-stock-alert',
    templateUrl: './stock-alert.component.html',
    styleUrls: ['./stock-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockAlertComponent implements OnInit, OnChanges, OnDestroy {
    @Input() product: IDBProduct;
    /// The variant and size on screen (the product page's selectedPriceOption).
    @Input() option: any;
    /// How the page names that option ("No Shell · 868MHz"), or '' for a product with one. Shown here only: the server
    /// names the option in the email itself.
    @Input() optionLabel = '';
    /// The page does not know yet whether an alert can be offered to this visitor (their country is not in): the box
    /// holds its place and shows nothing, rather than a form that might go away.
    @Input() waiting = false;

    private static count = 0;
    /// Keeps the field's id unique when the quick view and the page are both open.
    readonly uid = ++StockAlertComponent.count;

    /// The browser has drawn it; the server render shows a placeholder of the same height.
    ready = false;
    /// What is set already is known: the account's alerts, or this browser's kept ones checked with the server. Until
    /// then the placeholder, so the box never shows the form and then "you're on the list", or the other way round.
    loaded = false;
    state: 'idle' | 'set' | 'in-stock' | 'unavailable' = 'idle';
    busy = false;
    error = '';
    emailError = '';
    /// Where the email of the alert that is set will go, masked.
    shownEmail = '';
    /// What cancels the alert that is set; '' when this browser was not given one (already set elsewhere).
    ticket = '';

    signedIn = false;
    /// The account's own address, which a signed-in shopper's alerts go to - null while unknown, or if it has none.
    accountEmail: string = null;

    readonly email = new FormControl('');
    /// The field people never see; a script fills it, and is told it worked.
    readonly website = new FormControl('');

    private token = '';
    private retries = 0;
    private mine: StockAlertsMine['Alerts'] = [];
    private readonly destroy$ = new Subject<void>();
    private destroyed = false;

    private static readonly StorageKey = 'izz_stock_alerts';
    private static readonly KeepAtMost = 40;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private alerts: StockAlertService,
        private user: UserService,
        private analytics: AnalyticsService,
        private storage: StorageService,
        private cd: ChangeDetectorRef,
        private culture: CultureService,
    ) { }

    ngOnInit(): void {
        if (!isPlatformBrowser(this.platformId) || !this.product) {
            return;
        }
        this.ready = true;
        this.signedIn = this.user.isLoggedIn();

        if (this.signedIn) {
            this.alerts.mine(this.product.ID).pipe(takeUntil(this.destroy$)).subscribe(res => {
                if (res && res.SignedIn) {
                    this.accountEmail = res.Email || null;
                    this.mine = res.Alerts || [];
                } else {
                    // The sign-in has run out: a guest's form, then.
                    this.signedIn = false;
                    this.fetchToken();
                }
                this.settle();
            }, () => this.settle() /* the form works without it: the server answers "already" for an alert that is set */);
            return;
        }

        this.fetchToken();
        // What this browser kept may have been sent, or have run out, since: asked before it is shown.
        const tickets = this.keptTickets();
        if (tickets.length === 0) {
            this.settle();
            return;
        }
        this.alerts.check(tickets).pipe(takeUntil(this.destroy$)).subscribe(res => {
            if (res && Array.isArray(res.Waiting)) {
                this.keepOnly(tickets, res.Waiting);
            }
            this.settle();
        }, () => this.settle());
    }

    private settle(): void {
        this.loaded = true;
        this.showKnown();
        this.cd.markForCheck();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.option && !changes.option.firstChange && this.ready) {
            this.error = '';
            this.emailError = '';
            this.showKnown();
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
    }

    /// A signed-in shopper whose account has an address types nothing.
    get asksForEmail(): boolean {
        return !this.signedIn || !this.accountEmail;
    }

    get accountShown(): string {
        return StockAlertComponent.mask(this.accountEmail);
    }

    submit(): void {
        if (this.busy) {
            return;
        }
        this.error = '';
        this.emailError = '';
        if (this.asksForEmail && !StockAlertComponent.looksLikeEmail(this.typedEmail())) {
            this.emailError = this.culture.t('enter-valid-email', 'Enter a valid email address');
            return;
        }

        this.busy = true;
        this.retries = 0;
        if (this.signedIn || this.token) {
            this.post();
        } else {
            // The token did not come when the box opened: ask now, then send.
            this.alerts.token().pipe(takeUntil(this.destroy$)).subscribe(
                token => { this.token = token || ''; this.post(); },
                () => this.failed());
        }
    }

    cancel(): void {
        if (this.busy || !this.ticket) {
            return;
        }
        this.busy = true;
        this.error = '';
        const ticket = this.ticket;
        this.alerts.cancel(ticket).pipe(takeUntil(this.destroy$)).subscribe(answer => {
            this.busy = false;
            if (answer && (answer.Result === 'cancelled' || answer.Result === 'gone')) {
                this.forget(ticket);
                this.state = 'idle';
                this.ticket = '';
                this.shownEmail = '';
            } else {
                this.error = answer && answer.Result === 'too-many'
                    ? this.culture.t('stock-alert-too-many-tries', 'Too many tries from here just now. Please try again later.')
                    : this.culture.t('didnt-go-through-try-again', 'That didn\'t go through. Please try again.');
            }
            this.cd.markForCheck();
        }, () => {
            this.busy = false;
            this.error = this.culture.t('didnt-go-through-check-connection', 'That didn\'t go through. Please check your connection and try again.');
            this.cd.markForCheck();
        });
    }

    reload(): void {
        if (isPlatformBrowser(this.platformId)) {
            location.reload();
        }
    }

    // ---- sending ----------------------------------------------------------------------------------

    private post(): void {
        this.alerts.subscribe({
            ProductId: this.product.ID,
            OptionId: this.optionId,
            Email: this.asksForEmail ? this.typedEmail() : '',
            Token: this.token,
            Website: String(this.website.value ?? ''),
        }).pipe(takeUntil(this.destroy$)).subscribe(answer => this.answered(answer), () => this.failed());
    }

    private answered(answer: StockAlertAnswer): void {
        switch (answer && answer.Result) {
            case 'subscribed':
            case 'already':
                this.busy = false;
                this.state = 'set';
                this.ticket = answer.Ticket || '';
                this.shownEmail = StockAlertComponent.mask(answer.Email || this.typedEmail() || this.accountEmail);
                this.remember(answer);
                if (answer.Result === 'subscribed') {
                    this.analytics.stockAlert(this.product, this.option);
                }
                break;
            case 'in-stock':
                this.busy = false;
                this.state = 'in-stock';
                break;
            case 'unavailable':
                this.busy = false;
                this.state = 'unavailable';
                break;
            case 'invalid':
                this.busy = false;
                if (answer.Errors && answer.Errors.email) {
                    if (!this.asksForEmail) {
                        // Signed in on this page but not on the server any more: the box asks for an address.
                        this.signedIn = false;
                        this.accountEmail = null;
                    }
                    this.emailError = answer.Errors.email;
                } else {
                    this.error = this.culture.t('stock-alert-choose-option', 'Choose one of the options above, then try again.');
                }
                break;
            case 'retry':
                // The token was from before a deploy, or sent faster than a person types: the server hands back a good
                // one and says how long to wait - done here, the shopper only sees the button working.
                this.token = answer.Token || this.token;
                if (this.retries++ < 2) {
                    setTimeout(() => { if (!this.destroyed) { this.post(); } }, Math.min(10, Math.max(1, answer.RetryAfter || 3)) * 1000 + 250);
                    return;
                }
                this.failed();
                return;
            case 'too-many':
                this.busy = false;
                this.error = this.culture.t('stock-alert-too-many', 'Too many alerts from here just now. Please try again later.');
                break;
            default:
                this.failed();
                return;
        }
        this.cd.markForCheck();
    }

    private failed(): void {
        this.busy = false;
        this.error = this.culture.t('didnt-go-through-check-connection', 'That didn\'t go through. Please check your connection and try again.');
        this.cd.markForCheck();
    }

    private fetchToken(): void {
        this.alerts.token().pipe(takeUntil(this.destroy$)).subscribe(token => this.token = token || '', () => this.token = '');
    }

    // ---- what is set already ----------------------------------------------------------------------

    private get optionId(): number {
        const id = this.option && this.option.ID;
        return id > 0 ? id : null;
    }

    /// The option on screen already has an alert - the account's, or one this browser made.
    private showKnown(): void {
        const optionId = this.optionId;
        const account = this.mine.find(a => (a.OptionId || null) === optionId);
        // A signed-in shopper goes by the account's own list; this browser's memory is a guest's.
        const kept = account || this.signedIn ? null : this.kept()[this.keyFor(optionId)];
        if (account) {
            this.state = 'set';
            this.ticket = account.Ticket || '';
            this.shownEmail = StockAlertComponent.mask(account.Email);
        } else if (kept) {
            this.state = 'set';
            this.ticket = kept.t;
            this.shownEmail = kept.e;
        } else if (this.state === 'set') {
            this.state = 'idle';
            this.ticket = '';
            this.shownEmail = '';
        }
    }

    private remember(answer: StockAlertAnswer): void {
        if (!answer.Ticket) {
            return;
        }
        if (this.signedIn) {
            this.mine = [...this.mine.filter(a => (a.OptionId || null) !== this.optionId),
                { OptionId: this.optionId, Ticket: answer.Ticket, Email: answer.Email }];
            return;
        }
        const kept = this.kept();
        kept[this.keyFor(this.optionId)] = { t: answer.Ticket, e: this.shownEmail, at: Date.now() };
        const newest = Object.keys(kept).sort((a, b) => kept[b].at - kept[a].at).slice(0, StockAlertComponent.KeepAtMost);
        this.save(newest.reduce((all, key) => { all[key] = kept[key]; return all; }, {} as { [key: string]: KeptAlert }));
    }

    private forget(ticket: string): void {
        this.mine = this.mine.filter(a => a.Ticket !== ticket);
        const kept = this.kept();
        Object.keys(kept).filter(key => kept[key].t === ticket).forEach(key => delete kept[key]);
        this.save(kept);
    }

    private keyFor(optionId: number): string {
        return this.product.ID + ':' + (optionId || 'any');
    }

    /// The tickets this browser kept for this product, any option.
    private keptTickets(): string[] {
        const kept = this.kept();
        const prefix = this.product.ID + ':';
        return Object.keys(kept).filter(key => key.startsWith(prefix) && kept[key] && kept[key].t).map(key => kept[key].t);
    }

    /// Forgets the kept alerts of these tickets that the server no longer has.
    private keepOnly(asked: string[], waiting: string[]): void {
        const gone = asked.filter(ticket => waiting.indexOf(ticket) < 0);
        if (gone.length === 0) {
            return;
        }
        const kept = this.kept();
        Object.keys(kept).filter(key => gone.indexOf(kept[key].t) >= 0).forEach(key => delete kept[key]);
        this.save(kept);
    }

    private kept(): { [key: string]: KeptAlert } {
        try {
            const raw = this.storage.getItem(StockAlertComponent.StorageKey);
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }

    private save(kept: { [key: string]: KeptAlert }): void {
        try {
            this.storage.setItem(StockAlertComponent.StorageKey, JSON.stringify(kept));
        } catch { /* a browser that keeps nothing simply does not remember it; the alert is on the server */ }
    }

    private typedEmail(): string {
        return String(this.email.value ?? '').trim();
    }

    /// The server's own check is the one that counts (NewsletterSignup.IsValid); this only saves a round trip.
    static looksLikeEmail(text: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text || '') && (text || '').length <= 254;
    }

    /// "s•••@example.com": enough to recognise, not the whole address on a screen someone may be looking at.
    static mask(email: string): string {
        const text = (email || '').trim();
        const at = text.indexOf('@');
        return at < 1 ? text : text[0] + '•••' + text.slice(at);
    }
}
